import { useMemo } from "react";
import { Plus } from "lucide-react";
import type { TimeSeriesEntry, ColumnDef } from "./types";
import ComputedScheduleTable from "./ComputedScheduleTable";
import TimeSeriesInputTable from "./TimeSeriesInputTable";

// ---------- types ----------

interface ProjectedExpenseRow {
  year: number;
  calendar_year: number;
  base_amount: number;
  growth_rate: number;
  projected_amount: number;
  formula: string;
}

export interface ExpenseLineItem {
  name: string;
  amount: number;
}

interface ExpenseDetailPanelProps {
  category: string;
  currentAmount: number;
  growthRate: number;
  lineItems?: ExpenseLineItem[];
  entries: TimeSeriesEntry[];
  onEntryChange: (entryId: string, key: string, value: unknown) => void;
  onEntryAdd: (template: Record<string, unknown>) => void;
  onEntryDelete: (entryId: string) => void;
  onSave: () => Promise<void>;
  onReset: () => void;
  isDirty: boolean;
  isSaving: boolean;
  projectedSchedule: ProjectedExpenseRow[];
}

// ---------- schedule columns ----------

const SCHEDULE_COLUMNS = [
  { key: "year", header: "Year", type: "year" as const, width: "60px" },
  { key: "calendar_year", header: "Calendar Year", type: "year" as const, width: "100px" },
  { key: "base_amount", header: "Base Amount", type: "currency" as const, width: "120px" },
  { key: "growth_rate", header: "Growth Rate", type: "percent" as const, width: "100px" },
  { key: "projected_amount", header: "Projected Amount", type: "currency" as const, width: "130px" },
  { key: "formula", header: "Formula", type: "text" as const },
];

// ---------- input columns ----------

const UNITS_OPTIONS = [
  { value: "$/Year", label: "$/Year" },
  { value: "$/SF/Year", label: "$/SF/Year" },
  { value: "$/Month", label: "$/Month" },
  { value: "$/SF/Month", label: "$/SF/Month" },
];

const INPUT_COLUMNS: ColumnDef[] = [
  { key: "start_date", header: "Year", type: "year", width: "80px" },
  { key: "amount", header: "Amount", type: "currency", width: "120px" },
  {
    key: "units",
    header: "Units",
    type: "dropdown",
    options: UNITS_OPTIONS,
    width: "120px",
  },
  { key: "growth_rate", header: "Growth Rate", type: "percent", width: "100px", decimals: 2 },
  { key: "override", header: "Override", type: "checkbox", width: "80px" },
];

// ---------- helpers ----------

function parseYear(v: string | null | undefined): number | null {
  if (!v) return null;
  // If it's a 4-digit year string, parse directly (avoid Date constructor)
  const yearMatch = v.match(/^(\d{4})$/);
  if (yearMatch) return parseInt(yearMatch[1], 10);
  // If it's an ISO date string, extract year
  const isoMatch = v.match(/^(\d{4})-/);
  if (isoMatch) return parseInt(isoMatch[1], 10);
  // Fallback: try parseInt
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

function entriesToRows(entries: TimeSeriesEntry[]) {
  return entries.map((entry) => ({
    id: entry.entry_id,
    source: entry.source,
    values: {
      start_date: parseYear(entry.start_date),
      amount: entry.amount,
      units: entry.units,
      growth_rate: entry.extra?.growth_rate ?? null,
      override: entry.extra?.override ?? false,
    },
  }));
}

// ---------- component ----------

const fmtCurrency = (val: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);

export default function ExpenseDetailPanel({
  category,
  currentAmount,
  growthRate,
  lineItems,
  entries,
  onEntryChange,
  onEntryAdd,
  onEntryDelete,
  onSave,
  onReset,
  isDirty,
  isSaving,
  projectedSchedule,
}: ExpenseDetailPanelProps) {
  const rows = useMemo(() => entriesToRows(entries), [entries]);

  const scheduleRows = useMemo(
    () =>
      projectedSchedule.map((row) => ({
        year: row.year,
        calendar_year: row.calendar_year,
        base_amount: row.base_amount,
        growth_rate: row.growth_rate,
        projected_amount: row.projected_amount,
        formula: row.formula,
      })),
    [projectedSchedule],
  );

  const actionButtons = useMemo(
    () => [
      {
        label: "+ Add Year",
        icon: <Plus className="w-3.5 h-3.5" />,
        rowTemplate: {
          start_date: new Date().getFullYear().toString(),
          amount: currentAmount,
          units: "$/Year",
          growth_rate: growthRate,
          override: false,
        },
        variant: "primary" as const,
      },
    ],
    [currentAmount, growthRate],
  );

  return (
    <div className="space-y-6">
      {/* Header summary */}
      <div className="bg-white border border-grove-300 rounded-xl p-4">
        <h3 className="font-display text-sm font-semibold text-bark-800 mb-2">
          {category}
        </h3>
        <div className="flex items-center gap-6 text-sm text-bark-500">
          <span>
            Current Amount:{" "}
            <span className="font-medium text-bark-700 tabular-nums">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              }).format(currentAmount)}
            </span>
          </span>
          <span>
            Growth Rate:{" "}
            <span className="font-medium text-bark-700 tabular-nums">
              {(growthRate * 100).toFixed(1)}%
            </span>
          </span>
        </div>
      </div>

      {/* Line item breakdown */}
      {lineItems && lineItems.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2">
            Line Item Breakdown (Base Year)
          </h4>
          <div className="bg-white border border-grove-300 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-grove-50 border-b border-grove-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-bark-500">
                    Line Item
                  </th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-bark-500">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr
                    key={`${item.name}-${idx}`}
                    className="border-b border-grove-100 last:border-b-0"
                  >
                    <td className="px-4 py-2 text-bark-600">{item.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-bark-700 font-medium">
                      {fmtCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-grove-50/50 border-t border-grove-300">
                  <td className="px-4 py-2 font-semibold text-bark-700">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-bark-800">
                    {fmtCurrency(lineItems.reduce((sum, it) => sum + it.amount, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Projected schedule */}
      <div>
        <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2">
          Projected Expense Schedule
        </h4>
        <ComputedScheduleTable columns={SCHEDULE_COLUMNS} rows={scheduleRows} />
      </div>

      {/* Input table */}
      <div>
        <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2">
          Expense Inputs
        </h4>
        <TimeSeriesInputTable
          columns={INPUT_COLUMNS}
          rows={rows}
          onRowChange={onEntryChange}
          onRowAdd={onEntryAdd}
          onRowDelete={onEntryDelete}
          onReset={onReset}
          actionButtons={actionButtons}
          isDirty={isDirty}
          onSave={onSave}
          isSaving={isSaving}
        />
      </div>
    </div>
  );
}
